import { useState, useCallback, useEffect } from 'react';
import { RemoteParticipant, Room, RoomEvent } from 'livekit-client';
import {
  useCallAccessTokenControllerGetCallAccessTokenQuery,
  CallStatus,
} from '@shared/services/apiService/apiService';
import { resolveWebSocketUrl } from '@shared/utils/serviceUrl';
import { VITE_LIVEKIT_HOST } from '@shared/constants/environments';
import { CALLER_AUDIO_CAPTURE } from '@shared/constants/audioCapture';
import { RoomConnectionReturn } from '../types';

export const useRoomConnection = (
  callId: string,
  callStatus?: CallStatus,
  onError?: <T>(error: T) => void,
): RoomConnectionReturn => {
  const [room] = useState(new Room({ audioCaptureDefaults: CALLER_AUDIO_CAPTURE }));
  const [isRoomConnected, setIsRoomConnected] = useState(false);
  const [isConnecting, setIsConnecting] = useState(false);
  const [isMicEnabled, setIsMicEnabled] = useState(false);

  const shouldConnectToRoom = callStatus === 'in-progress';

  const { data: accessTokenData } = useCallAccessTokenControllerGetCallAccessTokenQuery(
    { id: callId },
    { skip: !shouldConnectToRoom },
  );

  const connectToRoom = useCallback(async () => {
    if (!accessTokenData?.token || isRoomConnected || isConnecting) return;

    try {
      setIsConnecting(true);
      const livekitServerUrl = resolveWebSocketUrl(VITE_LIVEKIT_HOST || 'ws://localhost:7880');
      await room.connect(livekitServerUrl, accessTokenData.token);
      setIsRoomConnected(true);
      setIsMicEnabled(false);
    } catch (error) {
      console.error('Failed to connect to room:', error);
      onError?.(error);
    } finally {
      setIsConnecting(false);
    }
  }, [accessTokenData?.token, isRoomConnected, isConnecting, room, onError]);

  const disconnectFromRoom = useCallback(() => {
    if (room.state === 'connected') {
      room.disconnect();
    }
    setIsRoomConnected(false);
    setIsConnecting(false);
  }, [room]);

  const toggleMicrophone = useCallback(async () => {
    if (room && room.localParticipant) {
      const newState = !isMicEnabled;
      await room.localParticipant.setMicrophoneEnabled(newState);
      setIsMicEnabled(newState);
    }
  }, [room, isMicEnabled]);

  // Setup room event listeners
  useEffect(() => {
    const onConnected = () => {
      setIsRoomConnected(true);
      setIsConnecting(false);
    };

    const onDisconnected = () => {
      setIsRoomConnected(false);
      setIsConnecting(false);
    };

    const onDeviceFailure = (error: Error) => {
      console.error('Device failure:', error);
      onError?.(error);
    };

    const onParticipantConnected = (participant: RemoteParticipant) => {
      console.log('Remote participant connected:', participant);
    };

    const onParticipantDisconnected = (participant: RemoteParticipant) => {
      console.log('Remote participant disconnected:', participant);
    };

    room.on(RoomEvent.Connected, onConnected);
    room.on(RoomEvent.Disconnected, onDisconnected);
    room.on(RoomEvent.MediaDevicesError, onDeviceFailure);
    room.on(RoomEvent.ParticipantConnected, onParticipantConnected);
    room.on(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);

    return () => {
      room.off(RoomEvent.Connected, onConnected);
      room.off(RoomEvent.Disconnected, onDisconnected);
      room.off(RoomEvent.MediaDevicesError, onDeviceFailure);
      room.off(RoomEvent.ParticipantConnected, onParticipantConnected);
      room.off(RoomEvent.ParticipantDisconnected, onParticipantDisconnected);
    };
  }, [room, onError]);

  useEffect(() => {
    if (!shouldConnectToRoom) {
      return;
    }

    if (accessTokenData?.token && !isRoomConnected && !isConnecting) {
      connectToRoom();
    }
  }, [accessTokenData?.token, isRoomConnected, isConnecting, connectToRoom, shouldConnectToRoom]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      disconnectFromRoom();
    };
  }, [callId, disconnectFromRoom]);

  return {
    room,
    isRoomConnected,
    isConnecting,
    isMicEnabled,
    toggleMicrophone,
    disconnectFromRoom,
  };
};
